import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import { Pagination, Response } from '@ui/src';
import { CustomerProps } from '../../../common/types';
import { individualCustomersApi } from '../services/individual';
import { applicationsApi } from '../../../new-application/store/services';
import { customersComponentApi } from '../../../customers/store/services/customers';
import { NewApplicationListItem } from '../../../new-application/types';

import ROUTES from '../../../../routes';

export interface IndividualListState {
  individualCustomers?: CustomerProps[];
  errorsList?: string;
  hintVisible: boolean;
  createdApplicationId?: string;
  selectedLists: string[];
  pagination?: Pagination;
}

const initialState: IndividualListState = {
  individualCustomers: undefined,
  hintVisible: false,
  selectedLists: [],
};

export const individualCustomersListSlice = createSlice({
  name: 'individualCustomersList',
  initialState,
  reducers: {
    setHintVisible: (state, action: PayloadAction<boolean>) => {
      state.hintVisible = action.payload;

      if (!action.payload) {
        state.createdApplicationId = undefined;
      }
    },
    selectList: (state, action: PayloadAction<string[]>) => {
      state.selectedLists = action.payload;
    },
    clearIndividualList: (state) => {
      state.individualCustomers = undefined;
      state.errorsList = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        individualCustomersApi.endpoints.getIndividualUsers.matchPending,
        (state) => {
          state.errorsList = undefined;
        },
      )
      .addMatcher(
        individualCustomersApi.endpoints.getIndividualUsers.matchRejected,
        (state, action: any) => {
          state.individualCustomers = undefined;
          state.pagination = undefined;

          if (action?.payload.data) {
            if (Array.isArray(action?.payload.data?.message)) {
              state.errorsList = action?.payload.data?.message
                ?.map((item: { value: string }) => item.value)
                .join('; ');
            } else {
              state.errorsList = action?.payload.data?.error;
            }
          }
        },
      )
      .addMatcher(
        individualCustomersApi.endpoints.getIndividualUsers.matchFulfilled,
        (state, action: PayloadAction<Response<CustomerProps>>) => {
          state.individualCustomers = action.payload.data.edges.map((item) => {
            const result = {
              ...item.node,
              ids: item?.node?.id,
              phone: item.node?.user?.phone,
              email: item.node?.user?.email,
              dob: item?.node?.user?.profile?.dob,
              search: `${item.node?.firstName} ${item?.node.lastName}`,
              url: generatePath(
                ROUTES.CUSTOMERS_INDIVIDUAL_OVERVIEW_TRANSACTIONS,
                {
                  customerId: item.node.id,
                },
              ),
            };

            return result;
          });

          state.pagination = action.payload.data.pageInfo;
        },
      )
      .addMatcher(
        individualCustomersApi.endpoints.updateIndividualUserBaseInformation
          .matchFulfilled,
        (state, action: PayloadAction<{ data: CustomerProps }>) => {
          const nextIndividualCustomers = state.individualCustomers?.map(
            (item) => {
              const result = { ...item };

              if (result.id === action.payload.data.id) {
                result.firstName = action.payload.data.firstName;
                result.lastName = action.payload.data.lastName;
              }

              return result;
            },
          );

          state.individualCustomers = nextIndividualCustomers;
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.closeOrganization.matchFulfilled,
        (state, action: PayloadAction<{ data: CustomerProps }>) => {
          const nextIndividualCustomers = state.individualCustomers?.map(
            (item) => {
              const result = { ...item };

              if (result.id === action.payload.data.id) {
                result.status = action.payload.data.status;
              }

              return result;
            },
          );

          state.individualCustomers = nextIndividualCustomers;
        },
      )
      .addMatcher(
        customersComponentApi.endpoints.reopenOrganization.matchFulfilled,
        (state, action: PayloadAction<{ data: CustomerProps }>) => {
          const nextIndividualCustomers = state.individualCustomers?.map(
            (item) => {
              const result = { ...item };

              if (result.id === action.payload.data.id) {
                result.status = action.payload.data.status;
              }

              return result;
            },
          );

          state.individualCustomers = nextIndividualCustomers;
        },
      )
      .addMatcher(
        applicationsApi.endpoints.createApplication.matchFulfilled,
        (state, action: PayloadAction<{ data: NewApplicationListItem }>) => {
          state.createdApplicationId = action.payload.data.id;
        },
      );
  },
});

export const { selectList, setHintVisible, clearIndividualList } =
  individualCustomersListSlice.actions;

export default individualCustomersListSlice.reducer;
